import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Check, X, Save, Edit, User, Shield } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

const TeamPairings = ({ fixture, onSave, onClose }) => {
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [pairings, setPairings] = useState({
    home: { player1: null, player2: null },
    away: { player1: null, player2: null }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPlayers();
  }, [fixture]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      console.log('Fetching players for:', { homeTeam: fixture.homeTeam, awayTeam: fixture.awayTeam, homeTeamId: fixture.homeTeamId, awayTeamId: fixture.awayTeamId });
      
      // Fetch all players and filter by teamId
      const playersSnapshot = await getDocs(collection(db, 'players'));
      const allPlayers = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('All players:', allPlayers);
      
      // Filter home team players by teamId
      const homeTeamPlayers = allPlayers.filter(player => 
        player.teamId === fixture.homeTeamId || 
        player.team === fixture.homeTeam
      );
      
      // Filter away team players by teamId
      const awayTeamPlayers = allPlayers.filter(player => 
        player.teamId === fixture.awayTeamId || 
        player.team === fixture.awayTeam
      );

      console.log('Home team players:', homeTeamPlayers);
      console.log('Away team players:', awayTeamPlayers);

      setHomePlayers(homeTeamPlayers);
      setAwayPlayers(awayTeamPlayers);
      
      // Load existing pairings if any
      if (fixture.selectedPlayers) {
        setPairings(fixture.selectedPlayers);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching players:', err);
      setError('Failed to load players');
      setLoading(false);
    }
  };

  const handlePlayerSelect = (team, playerKey, player) => {
    setPairings(prev => ({
      ...prev,
      [team]: {
        ...prev[team],
        [playerKey]: player
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Validate that all 4 players are selected
      if (!pairings.home.player1 || !pairings.home.player2 || 
          !pairings.away.player1 || !pairings.away.player2) {
        setError('Please select 2 players from each team');
        return;
      }

      // Save pairings to fixture
      await onSave(pairings);
      onClose();
    } catch (err) {
      setError('Failed to save pairings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isPairingComplete = () => {
    return pairings.home.player1 && pairings.home.player2 && 
           pairings.away.player1 && pairings.away.player2;
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1C563D' }}>
            Loading players...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
      overflowY: 'auto'
    }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          background: 'white',
          borderRadius: '1.5rem',
          padding: '1.5rem',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          margin: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1C563D', marginBottom: '0.5rem' }}>
              Set Team Pairings
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              {fixture.homeTeam} vs {fixture.awayTeam}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#F3F4F6',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#6B7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            color: '#DC2626',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        {/* Team Pairings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          {/* Home Team */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
              borderRadius: '0.75rem'
            }}>
              <Shield size={20} style={{ color: '#10B981' }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#065F46', margin: 0 }}>
                {fixture.homeTeam}
              </h3>
            </div>

            {/* Home Team Player 1 */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                Player 1
              </label>
              <select
                value={pairings.home.player1?.id || ''}
                onChange={(e) => {
                  const player = homePlayers.find(p => p.id === e.target.value);
                  handlePlayerSelect('home', 'player1', player);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #E5E7EB',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="">Select Player 1...</option>
                {homePlayers.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.category || 'Player'})
                  </option>
                ))}
              </select>
              {pairings.home.player1 && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  background: '#F0FDF4',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: '#065F46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Check size={16} />
                  {pairings.home.player1.name} selected
                </div>
              )}
            </div>

            {/* Home Team Player 2 */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                Player 2
              </label>
              <select
                value={pairings.home.player2?.id || ''}
                onChange={(e) => {
                  const player = homePlayers.find(p => p.id === e.target.value);
                  handlePlayerSelect('home', 'player2', player);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #E5E7EB',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="">Select Player 2...</option>
                {homePlayers.filter(p => p.id !== pairings.home.player1?.id).map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.category || 'Player'})
                  </option>
                ))}
              </select>
              {pairings.home.player2 && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  background: '#F0FDF4',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: '#065F46',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Check size={16} />
                  {pairings.home.player2.name} selected
                </div>
              )}
            </div>
          </div>

          {/* Away Team */}
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              padding: '0.75rem',
              background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
              borderRadius: '0.75rem'
            }}>
              <Shield size={20} style={{ color: '#3B82F6' }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1E40AF', margin: 0 }}>
                {fixture.awayTeam}
              </h3>
            </div>

            {/* Away Team Player 1 */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                Player 1
              </label>
              <select
                value={pairings.away.player1?.id || ''}
                onChange={(e) => {
                  const player = awayPlayers.find(p => p.id === e.target.value);
                  handlePlayerSelect('away', 'player1', player);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #E5E7EB',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="">Select Player 1...</option>
                {awayPlayers.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.category || 'Player'})
                  </option>
                ))}
              </select>
              {pairings.away.player1 && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  background: '#EFF6FF',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: '#1E40AF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Check size={16} />
                  {pairings.away.player1.name} selected
                </div>
              )}
            </div>

            {/* Away Team Player 2 */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                Player 2
              </label>
              <select
                value={pairings.away.player2?.id || ''}
                onChange={(e) => {
                  const player = awayPlayers.find(p => p.id === e.target.value);
                  handlePlayerSelect('away', 'player2', player);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #E5E7EB',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <option value="">Select Player 2...</option>
                {awayPlayers.filter(p => p.id !== pairings.away.player1?.id).map(player => (
                  <option key={player.id} value={player.id}>
                    {player.name} ({player.category || 'Player'})
                  </option>
                ))}
              </select>
              {pairings.away.player2 && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  background: '#EFF6FF',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: '#1E40AF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Check size={16} />
                  {pairings.away.player2.name} selected
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pairing Summary */}
        {isPairingComplete() && (
          <div style={{
            background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
            borderRadius: '0.75rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            border: '1px solid #10B981'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#065F46', marginBottom: '0.75rem' }}>
              Pairing Summary
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
              <div>
                <div style={{ fontWeight: '600', color: '#065F46', marginBottom: '0.25rem' }}>
                  {fixture.homeTeam}
                </div>
                <div style={{ color: '#374151' }}>
                  {pairings.home.player1?.name} & {pairings.home.player2?.name}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#1E40AF', marginBottom: '0.25rem' }}>
                  {fixture.awayTeam}
                </div>
                <div style={{ color: '#374151' }}>
                  {pairings.away.player1?.name} & {pairings.away.player2?.name}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !isPairingComplete()}
            style={{
              padding: '0.75rem 1.5rem',
              background: saving || !isPairingComplete() ? '#9CA3AF' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: saving || !isPairingComplete() ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {saving ? (
              <>
                <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Pairings
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default TeamPairings;
